import { Link } from 'react-router-dom'
import { CheckCircle, Info } from 'lucide-react'
import { cn } from '../../lib/utils'
import {
  CLIENT_LIMIT_NOTE,
  FOUNDING_PARTNER,
  INCLUDED_IN_EVERY_PLAN,
  NOT_BUILT_YET,
  PRICING_PLANS,
  formatPrice,
} from '../../data/pricing'

interface PricingPlansProps {
  /** Show the founding-partner offer above the plans (default true). */
  showFoundingOffer?: boolean
  /** Where the plan buttons go (default /register). */
  ctaTo?: string
  className?: string
}

/**
 * The one pricing ladder (ADR-0029): Solo RP, Practice, Firm. Rendered by
 * every page that shows prices, from data/pricing.ts. No plan is badged as
 * "popular": we have no customers to make that claim with.
 */
export function PricingPlans({ showFoundingOffer = true, ctaTo = '/register', className }: PricingPlansProps) {
  return (
    <div className={cn('max-w-6xl mx-auto', className)} data-testid="pricing-plans">
      {showFoundingOffer && (
        <div className="mb-8 rounded-lg border border-blue-200 bg-blue-50 p-5 text-left" role="note" aria-label="Founding-partner offer">
          <p className="font-semibold text-blue-900">Founding partners: {FOUNDING_PARTNER.summary}</p>
          <p className="mt-1 text-sm text-blue-800">{FOUNDING_PARTNER.afterwards}</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {PRICING_PLANS.map((plan) => (
          <div key={plan.id} className="bg-white p-6 rounded-lg border-2 border-gray-200 shadow-lg flex flex-col">
            <div className="text-center mb-4">
              <h3 className="text-xl font-bold text-gray-900 mb-2">{plan.name}</h3>
              <div className="text-3xl font-bold text-gray-900 mb-2">
                {formatPrice(plan.priceMonthly)}
                <span className="text-lg text-gray-500">/mo</span>
              </div>
              <p className="text-sm text-gray-600">{plan.audience}</p>
            </div>
            <ul className="space-y-2 mb-6 text-sm flex-1" aria-label={`${plan.name} plan features`}>
              {plan.features.map((feature) => (
                <li key={feature} className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" aria-hidden="true" />
                  <span>{feature}</span>
                </li>
              ))}
              <li className="flex items-start text-gray-600">
                <CheckCircle className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" aria-hidden="true" />
                <span>Everything listed under &ldquo;Every plan includes&rdquo;</span>
              </li>
            </ul>
            <Link
              to={ctaTo}
              className="w-full bg-gray-900 hover:bg-gray-800 text-white py-3 rounded-lg font-semibold text-center transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 min-h-[44px] flex items-center justify-center"
              aria-label={`Start free on the ${plan.name} plan, ${formatPrice(plan.priceMonthly)} per month after the founding period`}
            >
              Start free
            </Link>
          </div>
        ))}
      </div>

      <div className="mt-8 rounded-lg border border-gray-200 bg-white p-6 text-left">
        <h3 className="text-lg font-semibold text-gray-900 mb-3">Every plan includes</h3>
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-700" aria-label="Included in every plan">
          {INCLUDED_IN_EVERY_PLAN.map((item) => (
            <li key={item} className="flex items-start">
              <CheckCircle className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" aria-hidden="true" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
        <div className="mt-4 space-y-1 text-sm text-gray-600">
          <p className="flex items-start">
            <Info className="h-4 w-4 text-gray-400 mr-2 mt-0.5 flex-shrink-0" aria-hidden="true" />
            <span>{CLIENT_LIMIT_NOTE}</span>
          </p>
          <p className="flex items-start">
            <Info className="h-4 w-4 text-gray-400 mr-2 mt-0.5 flex-shrink-0" aria-hidden="true" />
            <span>{NOT_BUILT_YET}</span>
          </p>
          <p className="flex items-start">
            <Info className="h-4 w-4 text-gray-400 mr-2 mt-0.5 flex-shrink-0" aria-hidden="true" />
            <span>Prices are per month, billed monthly in US dollars. Self-assessment readiness software: we don&apos;t certify anyone.</span>
          </p>
        </div>
      </div>
    </div>
  )
}
